/**
 * Shared "is the chat pinned to the newest message?" state, keyed by scroll
 * container element.
 *
 * ChatArea owns the rule (any user scroll UP releases the pin; scrolling back
 * down to the bottom re-arms it) and publishes it here. Everything else that
 * auto-scrolls the chat — AnimatedMessage's glide follower, MessageBubble's
 * per-part scroll — must check isPinReleased() first, otherwise it drags the
 * user back down while she is trying to read history.
 *
 * Releasing also fires an event so an in-flight word-by-word reveal can finish
 * instantly (same as tapping the message) instead of animating under her.
 */
const released = new WeakSet();
const RELEASE_EVENT = 'scrollpin:release';

export function releasePin(el) {
  if (!el) return;
  released.add(el);
  el.dispatchEvent(new CustomEvent(RELEASE_EVENT));
}

export function rearmPin(el) {
  if (el) released.delete(el);
}

export function isPinReleased(el) {
  return !!el && released.has(el);
}

/** Subscribe to user-scrolled-up on a container. Returns an unsubscribe fn. */
export function onPinRelease(el, handler) {
  if (!el) return () => {};
  el.addEventListener(RELEASE_EVENT, handler);
  return () => el.removeEventListener(RELEASE_EVENT, handler);
}

/**
 * Nearest ancestor that is a real scroll container (overflow-y auto/scroll).
 * Unlike the walks used for scrolling, this does NOT require the content to
 * overflow yet — a subscription made while the chat is still short must keep
 * working once it grows.
 */
export function findScrollContainer(node) {
  let el = node ? node.parentElement : null;
  while (el) {
    const oy = getComputedStyle(el).overflowY;
    if (oy === 'auto' || oy === 'scroll') return el;
    el = el.parentElement;
  }
  return null;
}
